import React, { useMemo, useState } from 'react';
import styles from './index.module.scss';
import _ from 'lodash';
import { IPurchaseOrder } from 'interfaces/purchaseOrder';
import { PO_STATUS, showError, showSuccess, showWarning } from 'utils';
import DataTable from 'components/DataTable';
import { tableStyles } from 'css/tableStyles';
import { editPOItemDetails } from 'services';
import ActionButton from 'components/ActionButton';
import DecimalInput from 'components/DecimalInput';
import Currency from 'components/Currency/Currency';
import * as yup from 'yup';
import DeleteActionForTable from 'components/DeleteActionForTableData';

const customStyles = {
  ...tableStyles,
  table: {
    style: {
      paddingBottom: '50px',
      backgroundColor: '#FAFAFA',
    },
  },
};

const editPOItemSchemaShape = {
  cost: yup.number().positive('Cost cannot be negative').typeError('cost must be a number').required('Cost cannot be empty'),
  price: yup.number().positive('Price cannot be negative').typeError('price must be a number').required('Price cannot be empty'),
  producerReceivedQuantity: yup.number().min(0, 'producerReceivedQuantity cannot be negative').typeError('producerReceivedQuantity must be a number').required('producerReceivedQuantity cannot be empty'),
  receivedQuantity: yup.number().min(0, 'receivedQuantity must be greater than zero').typeError('receivedQuantity must be a number'),
};
const editPOItemSchema = yup.object().shape(editPOItemSchemaShape);

const OrderItems: React.FC<IPurchaseOrder.IOrderItemsDetailsProps> = props => {
  const { items, poStatus, fetchPODetails, isPublic = false, onDeleteItem } = props;
  const [editingPOItemId, setEditingPOItemId] = useState('');
  const [poItemIdToBeEdited, setPoItemIdToBeEdited] = useState('');
  
  const handleSupplierQuantityChange = (item: Record<string, any>, value: string) => {
    item.receivedQuantity = parseFloat(value);
  };

  const handleProducerQuantityChange = (item: Record<string, any>, value: number) => {
    item.producerReceivedQuantity = value;
  };

  const handlePOItemPriceChange = (item: Record<string, any>, value: number) => {
    item.price = value;
  };

  const handlePOItemCostChange = (item: Record<string, any>, value: number) => {
    item.cost = value;
  };

  const getReceivedTotal = (row) => {
    return row.receivedTotal || _.round(row.cost * row.receivedQuantity, 2) || null;
  };

  const handlePOItemEditClick = async (item: Record<string, any>) => {
    if (poItemIdToBeEdited === item?.objectId) {
      if (poStatus === PO_STATUS.FULFILLED) {
        showWarning('Cannot edit as the Purchase Order is fulfilled.');
        return;
      }
      setEditingPOItemId(item.objectId);
      try {
        const valuesToBeUpdated = {
          price: item.price, 
          cost: item?.cost, 
          producerReceivedQuantity: item?.producerReceivedQuantity,
          receivedQuantity: item?.receivedQuantity,
        };
        await editPOItemSchema.validate(valuesToBeUpdated, { strict: true });
        await editPOItemDetails({ id: item.objectId, ...valuesToBeUpdated });
        showSuccess('Purchase Order Item updated successfully');
        fetchPODetails();
      } catch (e) {
        showError(e?.message || e);
      } finally {
        setPoItemIdToBeEdited('');
        setEditingPOItemId('');
      }
    } else {
      setPoItemIdToBeEdited(item?.objectId);
    }
  };

  const getPriceInput = (row) => {
    if (isPublic || poItemIdToBeEdited !== row?.objectId) {
      return <Currency amount={row.price} />;
    } else if (poItemIdToBeEdited === row?.objectId) {
      return (
        <DecimalInput
          isTableCell={true}
          disabled={false}
          value={row.price}
          onChange={val => handlePOItemPriceChange(row, val)}
        />
      );
    } else return null;
  };

  const getCostInput = (row) => {
    if (isPublic || poItemIdToBeEdited !== row?.objectId) {
      return <Currency amount={row.cost} />;
    } else if (poItemIdToBeEdited === row?.objectId) {
      return (
        <DecimalInput
          isTableCell={true}
          disabled={false}
          value={row.cost}
          onChange={val => handlePOItemCostChange(row, val)}
        />
      );
    } else return null;
  };

  const getEditPriceAction = (row) => {
    if (!isPublic) {
      return (
        <div className='d-flex'>
          <ActionButton
            text={poItemIdToBeEdited !== row?.objectId ? <i className='fa fa-pencil' /> : <i className='fa fa-save' />}
            isLoading={editingPOItemId === row?.objectId}
            active={editingPOItemId !== row?.objectId}
            onClick={() => handlePOItemEditClick(row)}
            customClass={styles['edit-po-item-btn']}
          />
          {poItemIdToBeEdited === row?.objectId && <ActionButton
            text={<i className={'fa fa-times'} />}
            isLoading={false}
            active={true}
            onClick={() => setPoItemIdToBeEdited('')}
            customClass={styles['edit-po-item-btn']}
          />}
        </div>
      );
    } else return null;
  };

  const getSupplierQuantityChangeAction = (row) => {
    if (!isPublic && poItemIdToBeEdited === row?.objectId) {
      return (
        <div className={styles['received-supplier-quantity']}>
          <DecimalInput
            isTableCell={true}
            value={row.receivedQuantity}
            onChange={val => handleSupplierQuantityChange(row, val)}
          />
        </div>
      );
    } else return <div>{row?.receivedQuantity || 'N/A'}</div>;
  };

  const getRecieverQuantityChangeAction = (row) => {
    if (!isPublic && poItemIdToBeEdited === row?.objectId) {
      return (
        <div className={styles['received-producer-quantity']}>
          <DecimalInput
            isTableCell={true}
            value={row.producerReceivedQuantity || '0'}
            onChange={val => handleProducerQuantityChange(row, val)}
          />
        </div>
      );
    } else {
      return <div>{(row.producerReceivedQuantity || 'N/A')}</div>;
    }
  };

  const columns = useMemo(() => [
    {
      name: 'POItemID',
      selector: 'objectId',
      width: '150px',
    },
    {
      name: 'ItemID',
      selector: 'item.objectId',
      width: '150px',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <a
          className={styles['link']}
          href={`/inventory-items/${row.item.objectId}`}
        >
          {row?.item?.objectId}
        </a>
      ),
    },
    {
      name: 'Item Name',
      selector: 'item.name',
      width: '200px',
    },
    {
      name: 'Inventory Name For Supplier',
      selector: 'inventoryNameForSupplier',
      width: '200px',
    },
    {
      name: 'Description',
      selector: 'description',
      omit: isPublic,
    },
    {
      name: 'Price',
      selector: 'price',
      // eslint-disable-next-line react/display-name
      cell: row => getPriceInput(row),
      omit: isPublic,
    },
    {
      name: 'Cost',
      selector: 'cost',
      format: row => getCostInput(row),
      omit: isPublic,
    },
    {
      name: 'Requested Quantity',
      selector: 'requestedQuantity',
    },
    {
      name: 'Received Supplier Quantity',
      selector: 'receivedQuantity',
      width: '180px',
      // eslint-disable-next-line react/display-name
      cell: row => getSupplierQuantityChangeAction(row),
      omit: isPublic,
    },
    {
      name: 'Supplier Unit',
      selector: 'supplierMapping',
      format: row => row?.supplierMapping || 'N/A',
    },
    {
      name: 'Received Producer Quantity',
      selector: 'producerReceivedQuantity',
      width: '180px',
      // eslint-disable-next-line react/display-name
      cell: row => getRecieverQuantityChangeAction(row),
      omit: isPublic,
    },
    {
      name: 'Producer Unit',
      selector: '',
      cell: row => row?.item?.unit?.description || 'N/A',
      omit: isPublic,
    },
    {
      name: 'Received Total',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <>
          <div> <Currency amount={getReceivedTotal(row)} /></div>
        </>
      ),
      omit: isPublic,
    },
    {
      name: '',
      width: '120px',
      cell: row => getEditPriceAction(row),
      omit: isPublic,
    },
    {
      name: 'Action',
      selector: '',
      sortable: true,
      width: '100px',
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        <div className={'ml-3'}>
          <DeleteActionForTable
            onClick={async()=>{onDeleteItem && await onDeleteItem(row)}}
          />
        </div>
      ),
      omit: !onDeleteItem,
    },
  ], [items, editingPOItemId, poItemIdToBeEdited]);

  return (
    <div className={styles['po-items-container']}>
      <DataTable
        data={items}
        noHeader={true}
        columns={columns}
        customStyles={customStyles}
        pagination={false}
        highlightOnHover={true}
      />
    </div>
  );
};

export default OrderItems;